import { useEffect, useRef, useState } from "react"
import { useLocation } from "react-router-dom"
import { 
  Imu,
  IsolatedScanIMU,
  IsolatedScanMotion,
  IsolatedScanThermal,
  IsolatedScanTHzMetadata,
  Motion,
  ProcessedTHzHeatmapImage,
  ScanMedia,
  SectionScan,
  Thermal,
} from "../types/types"
import { getIsolatedScanMetadata } from "../service/isolatedScanService"
import { getSectionScans } from "../service/sectionScanService"
import { getIsolatedScanImu } from "../service/isolatedScanImuService"
import { getIsolatedScanThermal } from "../service/isolatedScanThermalService"
import { getIsolatedScanMotion } from "../service/isolatedScanMotionService"
import { getScanMediaForSection } from "../service/scanMediaService"
import { getProcessedTHzHeatmapImage } from "../service/processedImages"
import { getDefaultProcessedTHzHeatmapImage } from "../service/scanAnalysisService"

/**
 * useLoadWorkflow is responsible for sequentially loading all required metadata for a workflow.
 */
const useLoadWorkflow = () => {

  const location = useLocation()
  const [sections, setSections] = useState<SectionScan[] | undefined>(undefined)
  const [thzMetadata, setThzMetadata] = useState<IsolatedScanTHzMetadata[][] | undefined>(undefined)
  const [imuData, setIMUData] = useState<Imu[][] | undefined>(undefined)
  const [thermalData, setThermalData] = useState<Thermal[][] | undefined>(undefined)
  const [motionData, setMotionData] = useState<Motion[][] | undefined>(undefined)
  const [images, setImages] = useState<ScanMedia[][] | undefined>(undefined)
  const [thzSectionHeatmapImages, setThzSectionHeatmapImages] = useState<ProcessedTHzHeatmapImage[] | undefined>(undefined)

  const [generalError, setGeneralError] = useState<Error | null>(null)
  const [imuDataError, setIMUDataError] = useState<Error | null>(null)
  const [thermalDataError, setThermalDataError] = useState<Error | null>(null)
  const [motionDataError, setMotionDataError] = useState<Error | null>(null)
  const [imagesError, setImagesError] = useState<Error | null>(null)
  const isLoadedRef = useRef(false)

  useEffect(() => {
    if (isLoadedRef.current) { return }
    const loadWorkflowData = async () => {
      isLoadedRef.current = true

      const workflow = location.state
      if (workflow === undefined) {
        setGeneralError(new Error("Could not find workflow"))
        return
      }

      const sectionResponse = await getSectionScans(workflow.id)
      if (sectionResponse instanceof Error) { 
        setGeneralError(sectionResponse)
        return
      }

      const sectionScans: SectionScan[] = sectionResponse
      setSections(sectionScans)

      if (sectionScans.length === 0) {
        setGeneralError(new Error("No sections found"))
        return
      }

      // Load first section thz heatmap and metadata with priority 1.
      const firstSectionId = sectionScans[0].id
      await Promise.all([
        setHeatmapsForSection(firstSectionId),
        setScanMetadataForSection(firstSectionId),
      ])
      
      // Load remaining sections thz heatmaps and metadata with priority 2.
      if (sectionScans.length > 1) {        
        const remainingSections = sectionScans.slice(1)
        for (let i=0; i<remainingSections.length; i++) {
          const section = remainingSections[i]
          await Promise.all([
            setHeatmapsForSection(section.id),
            setScanMetadataForSection(section.id),
          ])
        }
      }      

      // Load metal, thermal and motion data with priority 3.    
      for (let i=0; i<sectionScans.length; i++) {
        const section = sectionScans[i]
        await Promise.all([
          setImuForSection(section.id),
          setThermalForSection(section.id),
          setMotionForSection(section.id),
        ])
      }

      // Load images with priority 4.
      for (let i=0; i<sectionScans.length; i++) {
        const section = sectionScans[i]
        await setImagesForSection(section.id)
      }   
    }
    loadWorkflowData()
  }, [location.state])

  /**
   * Sets the heatmaps for a section.
   * 
   * @param sectionId of processed section to fetch
   */
  const setHeatmapsForSection = async (sectionId: number) => {
    let response = await getProcessedTHzHeatmapImage(sectionId)
    if (response instanceof Error) {
      if (response.cause !== "404") {
        setGeneralError(response)
        return
      }
      response = await getDefaultProcessedTHzHeatmapImage(sectionId)
      if (response instanceof Error) {
        setGeneralError(response)
        return
      }
    }

    // Convert 2d heatmap image into 3d THz heatmap image.
    const thz_heatmap_image = JSON.parse(response.thz_heatmap_image) as number[][]
    const heatmaps: number[][][] = []

    for (let i=0; i<thz_heatmap_image.length; i+=4) {
      const scan = []
      for (let sensor=0; sensor<4; sensor++) {
        scan.push(thz_heatmap_image[i+sensor])
      }
      heatmaps.push(scan)
    }    

    const sectionHeatmap: ProcessedTHzHeatmapImage = {
      id: response.id,
      section_scan_id: response.section_scan_id,
      processing_start_time: response.processing_start_time,
      processing_end_time: response.processing_end_time,
      version: response.version,
      thz_heatmap_image: heatmaps,
      vectors: JSON.parse(response.vectors)
    }

    setThzSectionHeatmapImages(prevState => {
      if (prevState === undefined) { return [sectionHeatmap] }
      const allScans = prevState.flat()
      const currentIds = [sectionHeatmap.id]
      const scanAlreadyLoaded = allScans.filter(s => {
        return currentIds.includes(s.id)
      })
      if (scanAlreadyLoaded.length > 0) { return prevState }

      return [...prevState, ...[sectionHeatmap]]
    })
  }

  /**
   * Sets all isolated scans metadata for a given section.
   * 
   * @param sectionId to fetch isolated scans metadata for
   * 
   * @return isolated scan metadata for the given section
   */
  const setScanMetadataForSection = async (sectionId: number) => {
    const response = await getIsolatedScanMetadata(sectionId)
    if (response instanceof Error) {
      setGeneralError(response)
      return
    }
    const metadata = response as IsolatedScanTHzMetadata[]

    setThzMetadata(prevState => {
      if (prevState === undefined) { return [metadata] }
      const allScans = prevState.flat()
      const currentIds = metadata.map(m => m.id)
      const scanAlreadyLoaded = allScans.filter(s => {
        return currentIds.includes(s.id)
      })
      if (scanAlreadyLoaded.length > 0) { return prevState }
      return [...prevState, ...[metadata]]
    })
  }

  /**
   * Sets imu for the given section.
   * 
   * @param sectionId to fetch isolated scan imu data for
   * 
   * @returns imu for the section
   */
  const setImuForSection = async (sectionId: number) => {
    const response = await getIsolatedScanImu(sectionId)
    if (response instanceof Error) {
      setIMUDataError(response)
      return
    }

    const imu = response.map((isolatedScanImu: IsolatedScanIMU) => {
      return {
        id: isolatedScanImu.id,
        section_scan_id: isolatedScanImu.section_scan_id,
        scan_no: isolatedScanImu.scan_no,
        seq_nos: JSON.parse(isolatedScanImu.seq_nos),
        metal: JSON.parse(isolatedScanImu.metal),
        roll_angles: JSON.parse(isolatedScanImu.roll_angles),
        pitch_angles: JSON.parse(isolatedScanImu.pitch_angles),
        inclination_angles: JSON.parse(isolatedScanImu.inclination_angles),
        accel_x: JSON.parse(isolatedScanImu.accel_x),
        accel_y: JSON.parse(isolatedScanImu.accel_y),
        accel_z: JSON.parse(isolatedScanImu.accel_z),
        gyro_x: JSON.parse(isolatedScanImu.gyro_x),
        gyro_y: JSON.parse(isolatedScanImu.gyro_y),
        gyro_z: JSON.parse(isolatedScanImu.gyro_z),
        date: isolatedScanImu.date,
      }
    })

    setIMUData(prevState => {
      if (prevState === undefined) { return [imu] }
      
      const allScans = prevState.flat()
      const currentIds = imu.map((m: Imu) => m.id)
      const scanAlreadyLoaded = allScans.filter(s => {
        return currentIds.includes(s.id)
      })
      if (scanAlreadyLoaded.length > 0) { return prevState }
      
      return [...prevState, ...[imu]]
    })
  }

/**
 * Sets thermal data for the given section.
 * 
 * @param sectionId to fetch isolated scan thermal data for
 * 
 * @returns thermal data for the section
 */
const setThermalForSection = async (sectionId: number) => {
  const response = await getIsolatedScanThermal(sectionId)
    if (response instanceof Error) {
      setThermalDataError(response)
      return
    }

    const thermal = response.map((isolatedScanThermal: IsolatedScanThermal) => {
      return {
        id: isolatedScanThermal.id,
        section_scan_id: isolatedScanThermal.section_scan_id,
        seq_nos: JSON.parse(isolatedScanThermal.seq_nos),
        sensor_1: JSON.parse(isolatedScanThermal.sensor_1),
        sensor_2: JSON.parse(isolatedScanThermal.sensor_2),
        scan_no: parseInt(isolatedScanThermal.scan_no),
        date: isolatedScanThermal.date
      }
    })


    setThermalData(prevState => {
      if (prevState === undefined) { return [thermal] }
      
      const allScans = prevState.flat()
      const currentIds = thermal.map((m: Thermal) => m.id)
      const scanAlreadyLoaded = allScans.filter(s => {
        return currentIds.includes(s.id)
      })
      if (scanAlreadyLoaded.length > 0) { return prevState }
      return [...prevState, ...[thermal]]
    })
  }

  /**
   * Sets motion data for the given section.
   * 
   * @param sectionScanId to fetch isolated scan motion data for
   * 
   * @returns motion data for the section
   */
  const setMotionForSection = async (sectionScanId: number) => {
    const response = await getIsolatedScanMotion(sectionScanId)
    if (response instanceof Error) {
      setMotionDataError(response)
      return
    }

    const motion = response.map((isolatedScanMotion: IsolatedScanMotion) => {
      return {
        id: isolatedScanMotion.id,
        section_scan_id: isolatedScanMotion.section_scan_id,
        seq_nos: JSON.parse(isolatedScanMotion.seq_nos),
        delta_x: JSON.parse(isolatedScanMotion.delta_x),
        delta_y: JSON.parse(isolatedScanMotion.delta_y),
        scan_no: parseInt(isolatedScanMotion.scan_no),
        date: isolatedScanMotion.date
      }
    })
    setMotionData(prevState => {
      if (prevState === undefined) { return [motion] }
      
      const allScans = prevState.flat()
      const currentIds = motion.map((m: Motion) => m.id)
      const scanAlreadyLoaded = allScans.filter(s => {
        return currentIds.includes(s.id)
      })
      if (scanAlreadyLoaded.length > 0) { return prevState }
      return [...(prevState ?? []), ...[motion]]
    })
  }

  /**
   * Sets images for the given section.
   * 
   * @param sectionId to fetch images for
   * 
   * @returns images for the section
   */
  const setImagesForSection = async (sectionId: number) => {
    const response = await getScanMediaForSection(sectionId)
    if (response instanceof Error) {
      setImagesError(response)
      return
    }

    const images = response as ScanMedia[]
    setImages(prevState => {
      if (prevState === undefined) { return [images] }
      
      const allScans = prevState.flat()
      const currentIds = images.map((m: ScanMedia) => m.id)
      const scanAlreadyLoaded = allScans.filter(s => {
        return currentIds.includes(s.id)
      })
      if (scanAlreadyLoaded.length > 0) { return prevState }

      return [...(prevState ?? []), ...[images]]
    })
  }

  return [{
    sections: {sections: sections, error: generalError},
    sectionTHzHeatmaps: {heatmaps: thzSectionHeatmapImages, error: generalError},
    thzMetadata: {metadata: thzMetadata, error: generalError},
    imuData: {samples: imuData, error: imuDataError},
    thermalData: {samples: thermalData, error: thermalDataError},
    motionData: {samples: motionData, error: motionDataError},
    scanMedia: {images: images, error: imagesError},
    generalError
  }]
}

export default useLoadWorkflow
